import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brochureDeliveryMethods } from '@/mocks/brochureData';

interface FormData {
  name: string;
  furigana: string;
  email: string;
  phone: string;
  deliveryMethod: string;
  interestedCourse: string;
  privacyAgreed: boolean;
}

const initialFormData: FormData = {
  name: '',
  furigana: '',
  email: '',
  phone: '',
  deliveryMethod: '',
  interestedCourse: '',
  privacyAgreed: false,
};

export default function BrochureForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const courseOptions = [
    { value: 'essential', label: t('brochure.form.course.essential') },
    { value: 'advanced', label: t('brochure.form.course.advanced') },
    { value: 'master', label: t('brochure.form.course.master') },
    { value: 'undecided', label: t('brochure.form.course.undecided') },
  ];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('brochure.form.nameError');
    if (!formData.furigana.trim()) newErrors.furigana = t('brochure.form.furiganaError');
    if (!formData.email.trim()) {
      newErrors.email = t('brochure.form.emailError');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('brochure.form.emailInvalidError');
    }
    if (!formData.deliveryMethod) newErrors.deliveryMethod = t('brochure.form.delivery');
    if (!formData.privacyAgreed) {
      newErrors.privacyAgreed = t('brochure.form.privacyError');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      navigate('/brochure/confirm', { state: { formData } });
    }
  };

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/40" id="brochure-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('brochure.form.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('brochure.form.title')}
          </h2>
          <p className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">
            {t('brochure.form.subtitleEn')}
          </p>
          <div className="gold-line mx-auto mb-5"></div>
          <p className="text-[15px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            {t('brochure.form.lead')}
          </p>
        </div>

        {/* No hard sell reassurance */}
        <div className="text-center mb-10 bg-background-50 border border-background-200/60 p-5">
          <p className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            {t('brochure.form.noHardSell', { defaultValue: '資料請求は無料です。ご希望のない方への過度なご連絡はいたしません。安心してお申込みください。' })}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-7">
            {/* Full Name */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('brochure.form.name')} <span className="text-accent-600 text-[12px]">{t('brochure.form.required')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('brochure.form.namePlaceholder')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            {/* Furigana */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('brochure.form.furigana')} <span className="text-accent-600 text-[12px]">{t('brochure.form.required')}</span>
              </label>
              <input
                type="text"
                name="furigana"
                value={formData.furigana}
                onChange={(e) => updateField('furigana', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('brochure.form.furiganaPlaceholder')}
              />
              {errors.furigana && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.furigana}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('brochure.form.email')} <span className="text-accent-600 text-[12px]">{t('brochure.form.required')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('brochure.form.emailPlaceholder')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('brochure.form.phone')} <span className="text-foreground-400 text-[12px]">{t('brochure.form.optional')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('brochure.form.phonePlaceholder')}
              />
            </div>

            {/* Delivery method */}
            <div>
              <p className="text-[14px] text-foreground-700 tracking-[0.06em] mb-3 font-label">
                {t('brochure.form.delivery')} <span className="text-accent-600 text-[12px]">{t('brochure.form.required')}</span>
              </p>
              <div className="space-y-2">
                {brochureDeliveryMethods.map((method) => (
                  <label
                    key={method.value}
                    className="flex items-start gap-3 p-4 bg-background-50 border border-background-200/60 hover:border-accent-500/30 cursor-pointer transition-colors duration-200"
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                          formData.deliveryMethod === method.value
                            ? 'border-accent-500'
                            : 'border-background-300'
                        }`}
                      >
                        {formData.deliveryMethod === method.value && (
                          <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                        )}
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value={method.value}
                      checked={formData.deliveryMethod === method.value}
                      onChange={(e) => updateField('deliveryMethod', e.target.value)}
                      className="sr-only"
                    />
                    <div>
                      <span className="block text-[16px] text-foreground-800 tracking-[0.04em] mb-1">
                        {t(`brochure.form.delivery.${method.value}`, { defaultValue: method.label })}
                      </span>
                      <span className="block text-[14px] text-foreground-500 tracking-[0.03em]">{t(`brochure.form.delivery.${method.value}.desc`, { defaultValue: method.description })}</span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.deliveryMethod && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.deliveryMethod}</p>
              )}
            </div>

            {/* Course of Interest */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('brochure.form.course')} <span className="text-foreground-400 text-[12px]">{t('brochure.form.optional')}</span>
              </label>
              <select
                name="interestedCourse"
                value={formData.interestedCourse}
                onChange={(e) => updateField('interestedCourse', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 appearance-none cursor-pointer"
                style={{
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%23888%27 stroke-width=%272%27%3E%3Cpolyline points=%276 9 12 15 18 9%27/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 16px center',
                  paddingRight: '40px',
                }}
              >
                <option value="">{t('brochure.form.course.undecided')}</option>
                {courseOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Privacy Policy agreement */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="privacyAgreed"
                  checked={formData.privacyAgreed}
                  onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-colors duration-200 ${
                    formData.privacyAgreed
                      ? 'bg-accent-500 border-accent-500'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {formData.privacyAgreed && (
                    <i className="ri-check-line text-[12px] text-primary-900"></i>
                  )}
                </div>
                <span className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
                  {t('brochure.form.privacyAgree')}{' '}
                  <Link to="/privacy" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors">
                    {t('brochure.form.privacy')}
                  </Link>
                  <span className="text-accent-600 text-[12px] ml-1">{t('brochure.form.required')}</span>
                </span>
              </label>
              {errors.privacyAgreed && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.privacyAgreed}</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('brochure.form.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
